import { isAdmStack, Stack } from '@pubstack/common/src/stack'
import { MappableAdUnit } from './mappingInventory'

/**
 * Which of the ad units on screen a media actually deploys, for the mapping filter.
 *
 * The mapping spreadsheet offers every site × ad unit couple the seat could take, and on a large scope
 * most of those ad units are never deployed on the media — they sit in the inventory and no stack
 * references them. The filter answers "is this one live anywhere on this media?" so the publisher can
 * hide the rows they will never fill.
 *
 * The answer exists for One only. A One stack names its ad units by id in its configuration; a Node
 * stack names none at all — `PbsStackConfiguration` is `{ contextId, bidderRuleId, floorRuleConfig }`,
 * and its ad units are derived at stack build time from the media's env. So there is nothing stored to
 * read back for Node, and `isAdUnitUsedInStacks` lets Node's ad units through rather than inventing an
 * answer: they are structurally carried by every media whose env matches, which
 * `canMediaCarryMappedAdUnit` has already decided next door.
 */

/**
 * The ad unit ids a media's One stacks deploy.
 *
 * Roadblock ad units live in their own arrays, mutually exclusive with the regular ones, so all four
 * are unioned — the same reading `StackStats.usedBy` makes on the server. Miss them and an ad
 * unit used only as a roadblock reads as unused.
 */
export const adUnitIdsInStacks = (stacks: Stack[]): Set<string> =>
  new Set(
    stacks
      .filter(isAdmStack)
      .flatMap(({ configuration }) => [
        ...configuration.desktopAdUnitsIds,
        ...configuration.mobileAdUnitsIds,
        ...(configuration.desktopRoadblockAdUnitsIds ?? []),
        ...(configuration.mobileRoadblockAdUnitsIds ?? []),
      ])
  )

/**
 * Whether the filter keeps this ad unit.
 *
 * Node's ad units always pass — see above. `product` is what says so, the same tag
 * `canMediaCarryMappedAdUnit` and the server's CSV builders read, and it matters here because a
 * cross-inventory Node seat is mapped over both collections at once.
 */
export const isAdUnitUsedInStacks = (adUnit: MappableAdUnit, stackedAdUnitIds: Set<string>): boolean => adUnit.product === 'pbs' || stackedAdUnitIds.has(adUnit.id)

/**
 * Whether the filter means anything for a seat, from its mapping dimensions.
 *
 * A seat mapped by placement has no ad unit rows to hide, and one not mapped by media has no media whose
 * stacks to read — either way there is nothing to filter, and `generateMappings` will ignore the
 * exclusion set anyway, since it only consults it for a combo carrying both a site and an ad unit.
 *
 * Shared rather than inlined at each end because the three ends are far apart and must agree: the seat
 * header decides whether to *render* the switch, the spreadsheet whether to apply it, and the "Mapping
 * options" flyout whether to warn that the CSV is filtered. The preference itself is stored per user,
 * not per seat, so a user who turns the filter on for one seat carries it to every other one — and where
 * the conditions drift, a seat whose header never offered the switch warns about it on download and then
 * hands out two identical files.
 *
 * Optional despite the type saying otherwise: `BidderSeat.mappingDimensions` is declared as a required
 * array, but the seats service, the flyout and the seat schema's own transform all guard against it
 * being absent, so stored seats predate the field. The seat header calls this on every render, where an
 * unguarded `.includes` would take the whole page down rather than one flyout.
 */
export const canFilterByStackedAdUnits = (mappingDimensions?: string[]): boolean => !!mappingDimensions?.includes('siteId') && !mappingDimensions.includes('placementId')
